import React, { useState, useEffect } from 'react';
import api from '../services/api';
import './Inventaire.css';

interface LigneInventaire {
  produitId: number;
  produitNom: string;
  codeBarres: string;
  categorie: string;
  stockTheorique: number;
  stockReel: number | null;
  ecart: number | null;
  valide: boolean;
}

interface Inventaire {
  id: number;
  reference: string;
  description: string;
  statut: string;
  lignes: LigneInventaire[];
  totalProduits: number;
  totalEcarts: number;
  utilisateur: string;
  notes: string;
  createdAt: string;
  termineAt: string;
}

interface Stats {
  totalProduits: number;
  produitsSaisis: number;
  produitsRestants: number;
  avecEcart: number;
  excedents: number;
  manquants: number;
  progression: number;
}

export default function InventairePage() {
  const [inventaires, setInventaires] = useState<Inventaire[]>([]);
  const [inventaireActif, setInventaireActif] = useState<Inventaire | null>(null);
  const [stats, setStats] = useState<Stats | null>(null);
  const [chargement, setChargement] = useState(true);
  const [showCreerModal, setShowCreerModal] = useState(false);
  const [showTerminerModal, setShowTerminerModal] = useState(false);
  const [showHistorique, setShowHistorique] = useState(false);
  const [description, setDescription] = useState('');
  const [notes, setNotes] = useState('');
  const [recherche, setRecherche] = useState('');
  const [filtreStatut, setFiltreStatut] = useState<'tous' | 'valide' | 'non_valide' | 'ecart'>('tous');
  const [appliquerCorrections, setAppliquerCorrections] = useState(true);
  const [erreur, setErreur] = useState('');
  const [succes, setSucces] = useState('');
  const [stocksReels, setStocksReels] = useState<Record<number, number>>({});

  useEffect(() => {
    chargerInventaires();
  }, []);

  const chargerInventaires = async () => {
    try {
      const res = await api.get('/inventaires');
      setInventaires(res.data);
      const actif = res.data.find((i: Inventaire) => i.statut === 'en_cours');
      if (actif) {
        setInventaireActif(actif);
        chargerStats(actif.id);
        const stocks: Record<number, number> = {};
        actif.lignes.forEach((l: LigneInventaire) => {
          if (l.stockReel !== null) stocks[l.produitId] = l.stockReel;
        });
        setStocksReels(stocks);
      }
    } catch (e) {
      setErreur('Impossible de charger les inventaires');
    } finally {
      setChargement(false);
    }
  };

  const chargerStats = async (id: number) => {
    try {
      const res = await api.get(`/inventaires/${id}/stats`);
      setStats(res.data);
    } catch (e) {}
  };

  const creerInventaire = async () => {
    try {
      const res = await api.post('/inventaires', { description, notes });
      setInventaireActif(res.data);
      setInventaires(prev => [res.data, ...prev]);
      setStocksReels({});
      setShowCreerModal(false);
      setDescription('');
      setNotes('');
      chargerStats(res.data.id);
      setSucces('Inventaire créé avec succès');
      setTimeout(() => setSucces(''), 3000);
    } catch (e) {
      setErreur("Erreur lors de la création de l'inventaire");
    }
  };

  const saisirStock = async (produitId: number, stockReel: number) => {
    if (!inventaireActif) return;
    try {
      const res = await api.put(`/inventaires/${inventaireActif.id}/ligne`, {
        produitId,
        stockReel,
      });
      setInventaireActif(res.data);
      chargerStats(inventaireActif.id);
    } catch (e) {}
  };

  const terminerInventaire = async () => {
    if (!inventaireActif) return;
    try {
      await api.put(`/inventaires/${inventaireActif.id}/terminer`, {
        appliquerCorrections,
      });
      setSucces(
        appliquerCorrections
          ? 'Inventaire terminé — stocks mis à jour automatiquement'
          : 'Inventaire terminé — aucune correction appliquée'
      );
      setShowTerminerModal(false);
      setInventaireActif(null);
      setStats(null);
      chargerInventaires();
      setTimeout(() => setSucces(''), 4000);
    } catch (e) {
      setErreur("Erreur lors de la clôture de l'inventaire");
    }
  };

  const annulerInventaire = async () => {
    if (!inventaireActif) return;
    if (!window.confirm("Voulez-vous annuler cet inventaire ? Aucune correction ne sera appliquée.")) return;
    try {
      await api.put(`/inventaires/${inventaireActif.id}/annuler`);
      setInventaireActif(null);
      setStats(null);
      chargerInventaires();
      setSucces('Inventaire annulé');
      setTimeout(() => setSucces(''), 3000);
    } catch (e) {
      setErreur("Erreur lors de l'annulation");
    }
  };

  const lignesFiltrees = inventaireActif?.lignes.filter(l => {
    const matchRecherche = recherche === '' ||
      l.produitNom.toLowerCase().includes(recherche.toLowerCase()) ||
      l.codeBarres?.toLowerCase().includes(recherche.toLowerCase()) ||
      l.categorie?.toLowerCase().includes(recherche.toLowerCase());
    const matchStatut =
      filtreStatut === 'tous' ? true :
      filtreStatut === 'valide' ? l.valide :
      filtreStatut === 'non_valide' ? !l.valide :
      filtreStatut === 'ecart' ? (l.valide && l.ecart !== 0) : true;
    return matchRecherche && matchStatut;
  }) || [];

  if (chargement) return <div className="page-chargement">Chargement...</div>;

  return (
    <div className="inventaire-page">
      <div className="page-header">
        <div>
          <h1>Inventaire physique</h1>
          <p>Comparez le stock théorique et le stock réel</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn-secondary" onClick={() => setShowHistorique(!showHistorique)}>
            📋 Historique
          </button>
          {!inventaireActif && (
            <button className="btn-primary" onClick={() => setShowCreerModal(true)}>
              + Nouvel inventaire
            </button>
          )}
          {inventaireActif && (
            <>
              <button className="btn-danger-outline" onClick={annulerInventaire}>
                ✕ Annuler
              </button>
              <button className="btn-terminer" onClick={() => setShowTerminerModal(true)}>
                ✅ Terminer l'inventaire
              </button>
            </>
          )}
        </div>
      </div>

      {succes && <div className="alert-succes">✅ {succes}</div>}
      {erreur && <div className="alert-erreur">⚠️ {erreur}</div>}

      {/* HISTORIQUE */}
      {showHistorique && (
        <div className="inv-historique">
          <div className="inv-historique-titre">📋 Historique des inventaires</div>
          {inventaires.length === 0 ? (
            <div className="inv-vide">Aucun inventaire effectué</div>
          ) : (
            <div className="inv-liste">
              {inventaires.map(inv => (
                <div key={inv.id} className="inv-item">
                  <div className="inv-item-info">
                    <div className="inv-ref">{inv.reference}</div>
                    <div className="inv-desc">{inv.description}</div>
                    <div className="inv-date">
                      {new Date(inv.createdAt).toLocaleDateString('fr-FR')}
                    </div>
                  </div>
                  <div className="inv-item-stats">
                    <span>{inv.totalProduits} produits</span>
                    <span>{inv.totalEcarts} écarts</span>
                  </div>
                  <span className={`inv-statut ${inv.statut}`}>
                    {inv.statut === 'en_cours' ? '🔄 En cours' :
                     inv.statut === 'termine' ? '✅ Terminé' : '✕ Annulé'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* PAS D'INVENTAIRE EN COURS */}
      {!inventaireActif && (
        <div className="inv-vide-card">
          <div className="ivc-icone">📦</div>
          <div className="ivc-titre">Aucun inventaire en cours</div>
          <div className="ivc-desc">
            Créez un nouvel inventaire pour comparer vos stocks théoriques et réels.
            NexaCom chargera automatiquement tous vos produits avec leurs stocks actuels.
          </div>
          <button className="btn-primary" onClick={() => setShowCreerModal(true)}>
            + Démarrer un inventaire
          </button>
        </div>
      )}

      {/* INVENTAIRE EN COURS */}
      {inventaireActif && (
        <>
          {/* HEADER */}
          <div className="inv-header-card">
            <div className="ihc-info">
              <div className="ihc-ref">{inventaireActif.reference}</div>
              <div className="ihc-desc">{inventaireActif.description}</div>
              <div className="ihc-date">
                Démarré le {new Date(inventaireActif.createdAt).toLocaleString('fr-FR')}
              </div>
            </div>
            {stats && (
              <div className="ihc-stats">
                <div className="ihc-stat">
                  <div className="ihc-stat-val">{stats.progression}%</div>
                  <div className="ihc-stat-lbl">Progression</div>
                </div>
                <div className="ihc-stat">
                  <div className="ihc-stat-val" style={{ color: '#3B6D11' }}>
                    {stats.produitsSaisis}
                  </div>
                  <div className="ihc-stat-lbl">Saisis</div>
                </div>
                <div className="ihc-stat">
                  <div className="ihc-stat-val" style={{ color: '#888' }}>
                    {stats.produitsRestants}
                  </div>
                  <div className="ihc-stat-lbl">Restants</div>
                </div>
                <div className="ihc-stat">
                  <div className="ihc-stat-val" style={{ color: '#A32D2D' }}>
                    {stats.avecEcart}
                  </div>
                  <div className="ihc-stat-lbl">Écarts</div>
                </div>
                <div className="ihc-stat">
                  <div className="ihc-stat-val" style={{ color: '#3B6D11' }}>
                    {stats.excedents}
                  </div>
                  <div className="ihc-stat-lbl">Excédents</div>
                </div>
                <div className="ihc-stat">
                  <div className="ihc-stat-val" style={{ color: '#854F0B' }}>
                    {stats.manquants}
                  </div>
                  <div className="ihc-stat-lbl">Manquants</div>
                </div>
              </div>
            )}
          </div>

          {/* BARRE DE PROGRESSION */}
          {stats && (
            <div className="inv-progression">
              <div className="inv-prog-barre">
                <div className="inv-prog-rempli"
                  style={{ width: `${stats.progression}%` }} />
              </div>
              <span>{stats.produitsSaisis} / {stats.totalProduits} produits saisis</span>
            </div>
          )}

          {/* FILTRES */}
          <div className="inv-filtres">
            <input
              value={recherche}
              onChange={e => setRecherche(e.target.value)}
              placeholder="🔍 Rechercher par produit, code-barres ou catégorie..."
              className="inv-search"
            />
            <div className="inv-filtre-tabs">
              {[
                { val: 'tous', label: 'Tous' },
                { val: 'non_valide', label: '⏳ À saisir' },
                { val: 'valide', label: '✓ Saisis' },
                { val: 'ecart', label: '⚠️ Écarts' },
              ].map(f => (
                <button key={f.val}
                  className={filtreStatut === f.val ? 'active' : ''}
                  onClick={() => setFiltreStatut(f.val as any)}>
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {/* TABLEAU */}
          <div className="inv-table">
            <table>
              <thead>
                <tr>
                  <th>Produit</th>
                  <th>Catégorie</th>
                  <th>Code-barres</th>
                  <th>Stock théorique</th>
                  <th>Stock réel (saisir)</th>
                  <th>Écart</th>
                  <th>Statut</th>
                </tr>
              </thead>
              <tbody>
                {lignesFiltrees.map(l => (
                  <tr key={l.produitId}
                    className={
                      !l.valide ? '' :
                      l.ecart === 0 ? 'ligne-ok' :
                      l.ecart! > 0 ? 'ligne-excedent' : 'ligne-manquant'
                    }>
                    <td className="inv-produit-nom">{l.produitNom}</td>
                    <td className="inv-cat">{l.categorie || '—'}</td>
                    <td className="inv-code">{l.codeBarres || '—'}</td>
                    <td className="inv-stock-theo">{l.stockTheorique}</td>
                    <td className="inv-stock-reel">
                      <div className="inv-saisie">
                        <input
                          type="number"
                          className="inv-input-reel"
                          value={
                            stocksReels[l.produitId] !== undefined
                              ? stocksReels[l.produitId]
                              : l.stockReel !== null ? l.stockReel : ''
                          }
                          onChange={e => {
                            const val = parseInt(e.target.value) || 0;
                            setStocksReels(prev => ({ ...prev, [l.produitId]: val }));
                          }}
                          onBlur={() => {
                            const val = stocksReels[l.produitId];
                            if (val !== undefined) saisirStock(l.produitId, val);
                          }}
                          onKeyDown={e => {
                            if (e.key === 'Enter') {
                              const val = stocksReels[l.produitId];
                              if (val !== undefined) saisirStock(l.produitId, val);
                            }
                          }}
                          min={0}
                          placeholder="0"
                        />
                        <button
                          className="btn-valider-ligne"
                          onClick={() => {
                            const val = stocksReels[l.produitId] ?? 0;
                            saisirStock(l.produitId, val);
                          }}>
                          ✓
                        </button>
                      </div>
                    </td>
                    <td>
                      {l.valide ? (
                        <span className={`inv-ecart ${l.ecart! > 0 ? 'positif' : l.ecart! < 0 ? 'negatif' : 'zero'}`}>
                          {l.ecart! > 0 ? '+' : ''}{l.ecart}
                        </span>
                      ) : (
                        <span className="inv-ecart-vide">—</span>
                      )}
                    </td>
                    <td>
                      {l.valide ? (
                        l.ecart === 0 ? (
                          <span className="inv-badge ok">✓ OK</span>
                        ) : l.ecart! > 0 ? (
                          <span className="inv-badge excedent">↑ Excédent</span>
                        ) : (
                          <span className="inv-badge manquant">↓ Manquant</span>
                        )
                      ) : (
                        <span className="inv-badge pending">⏳ À saisir</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {lignesFiltrees.length === 0 && (
              <div className="table-empty">Aucun produit ne correspond aux critères</div>
            )}
          </div>
        </>
      )}

      {/* MODAL CRÉER */}
      {showCreerModal && (
        <div className="modal-overlay" onClick={() => setShowCreerModal(false)}>
          <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📦 Nouvel inventaire</h2>
              <button className="modal-close" onClick={() => setShowCreerModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="inv-info-box">
                NexaCom va charger tous vos produits avec leurs stocks actuels.
                Vous pourrez saisir le stock réel pour chaque produit.
              </div>
              <div className="form-group">
                <label>Description</label>
                <input
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  placeholder={`Inventaire du ${new Date().toLocaleDateString('fr-FR')}`}
                />
              </div>
              <div className="form-group">
                <label>Notes</label>
                <textarea
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  rows={2}
                  placeholder="Observations, contexte de l'inventaire..."
                  style={{
                    width: '100%', padding: '8px',
                    border: '1px solid #E2E8F0', borderRadius: 7,
                    fontFamily: 'inherit', fontSize: 13,
                    boxSizing: 'border-box',
                  }}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowCreerModal(false)}>
                Annuler
              </button>
              <button className="btn-primary" onClick={creerInventaire}>
                🚀 Démarrer l'inventaire
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL TERMINER */}
      {showTerminerModal && inventaireActif && (
        <div className="modal-overlay" onClick={() => setShowTerminerModal(false)}>
          <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>✅ Terminer l'inventaire</h2>
              <button className="modal-close" onClick={() => setShowTerminerModal(false)}>×</button>
            </div>
            <div className="modal-body">
              {stats && (
                <div className="inv-recap">
                  <div className="ir-row">
                    <span>Produits saisis</span>
                    <span>{stats.produitsSaisis} / {stats.totalProduits}</span>
                  </div>
                  <div className="ir-row">
                    <span>Produits avec écart</span>
                    <span className={stats.avecEcart > 0 ? 'rouge' : 'vert'}>
                      {stats.avecEcart}
                    </span>
                  </div>
                  <div className="ir-row">
                    <span>Excédents</span>
                    <span style={{ color: '#3B6D11' }}>+{stats.excedents}</span>
                  </div>
                  <div className="ir-row">
                    <span>Manquants</span>
                    <span style={{ color: '#A32D2D' }}>-{stats.manquants}</span>
                  </div>
                </div>
              )}

              {stats && stats.produitsRestants > 0 && (
                <div className="inv-warning">
                  ⚠️ {stats.produitsRestants} produit{stats.produitsRestants > 1 ? 's' : ''} non saisi{stats.produitsRestants > 1 ? 's' : ''}.
                  Ces produits seront ignorés lors de la correction.
                </div>
              )}

              <div className="inv-correction-toggle">
                <label>
                  <input
                    type="checkbox"
                    checked={appliquerCorrections}
                    onChange={e => setAppliquerCorrections(e.target.checked)}
                  />
                  <div>
                    <div className="ict-titre">Appliquer les corrections de stock</div>
                    <div className="ict-desc">
                      Les stocks seront automatiquement ajustés selon les écarts constatés
                    </div>
                  </div>
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowTerminerModal(false)}>
                Annuler
              </button>
              <button className="btn-primary" onClick={terminerInventaire}>
                ✅ Confirmer la clôture
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}